/**
 * §22.7 — ReplayPlayer'ın SAF çekirdeği.
 *
 * Burada DOM, React ve ağ YOKTUR: zaman ekseni kurulumu, inaktivite sıkıştırma ve
 * timeline işaretçileri oynatıcıdan bağımsız test edilebilsin diye ayrıldı
 * (`replay-engine.test.ts`). Bileşen yalnızca "ne zaman ne uygulanır" kararını
 * buradan alır; "nasıl uygulanır" (iframe DOM'u) ReplayPlayer.tsx'te kalır.
 *
 * KRİTİK ZAMAN SÖZLEŞMESİ (§22.4/§22.6): chunk gövdesindeki `ts`, O CHUNK'ın
 * `started_at`'ine göre RÖLATİF ms'dir ve her chunk'ın ilk olayında 0'dır. Bir olayın
 * mutlak zamanı bu yüzden `chunk.started_at + event.ts`'tir — chunk'lar arası boşluk
 * (buffer modunda dakikalar sürebilir) ancak böyle doğru yerine oturur.
 */

/* -------------------------------------------------------------------------- */
/* Girdi tipleri                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Chunk gövdesindeki tek olay.
 *
 * Motor bilerek `@dxp/schema`'ya da `api.ts`'e de bağlanmaz: `ReplayEvent` (şema,
 * `t` daraltılmış birlik) ve `ReplayEventLite` (api, `t: string`) bu yapıya UYAR;
 * yapısal tip ikisini de kabul ederek saf çekirdeği taşımadan bağımsız tutar.
 */
export interface ReplayEventLike {
  t: string;
  ts: number;
  [key: string]: unknown;
}

/**
 * Zaman tabanı için gereken chunk alanları. Şemadaki `ReplayChunkMeta` bu yapıya
 * uyar; testler tam meta (mode/trigger/bytes) uydurmak zorunda kalmasın diye
 * yalnızca kullanılan iki alan istenir.
 */
export interface TimelineChunk {
  seq: number;
  /** chunk zaman tabanı — epoch ms (§22.6 `toUnixTimestamp64Milli`). */
  started_at: number;
}

/**
 * Chunk gövdeleri: chunk listesiyle AYNI SIRADA dizi ya da `seq` anahtarlı Map.
 * Dizi girdisi `null` olabilir — gövdesi çekilemeyen chunk kaydı çökertmez, atlanır
 * (§22.5 "replay kayıp-toleranslıdır").
 */
export type ChunkBodies =
  | ReadonlyArray<readonly ReplayEventLike[] | null | undefined>
  | ReadonlyMap<number, readonly ReplayEventLike[]>;

/* -------------------------------------------------------------------------- */
/* Zaman ekseni                                                                 */
/* -------------------------------------------------------------------------- */

/** Mutlak zamana taşınmış tek olay. */
export interface TimelineEvent {
  /** mutlak epoch ms = chunk.started_at + ev.ts (§22.4/§22.6) */
  at: number;
  ev: ReplayEventLike;
  /** hangi chunk'tan geldiği — hata ayıklama ve kararlı sıralama için */
  chunkSeq: number;
}

export interface Timeline {
  /** mutlak zamana göre artan sırada tüm olaylar */
  events: TimelineEvent[];
  /** ilk olayın mutlak zamanı (olay yoksa 0) */
  startAt: number;
  /** son olayın mutlak zamanı (olay yoksa 0) */
  endAt: number;
}

function readBody(
  bodies: ChunkBodies,
  seq: number,
  index: number,
): readonly ReplayEventLike[] | null {
  if (bodies instanceof Map) return bodies.get(seq) ?? null;
  const list = bodies as ReadonlyArray<readonly ReplayEventLike[] | null | undefined>;
  return list[index] ?? null;
}

/**
 * §22.4/§22.6 — chunk'ları tek bir mutlak zaman eksenine dizer.
 *
 * Sıralama önce `seq`e göre yapılır, sonra `at`e göre stabil sıralanır: aynı
 * milisaniyeye düşen olayların KAYIT SIRASI korunur (ör. bir mutation ile onu izleyen
 * click aynı ms'de olabilir; ters çevrilirse click bilinmeyen düğüme düşer).
 */
export function buildTimeline(chunks: readonly TimelineChunk[], bodies: ChunkBodies): Timeline {
  const ordered = chunks
    .map((chunk, index) => ({ chunk, events: readBody(bodies, chunk.seq, index) }))
    .sort((a, b) => a.chunk.seq - b.chunk.seq);

  const events: TimelineEvent[] = [];
  for (const { chunk, events: body } of ordered) {
    // Zaman tabanı yoksa bu chunk'ın olayları eksene yerleştirilemez — uydurmak yerine atla.
    if (body === null || !Number.isFinite(chunk.started_at)) continue;
    for (const ev of body) {
      if (ev === null || typeof ev !== 'object') continue;
      if (typeof ev.ts !== 'number' || !Number.isFinite(ev.ts)) continue;
      events.push({ at: chunk.started_at + ev.ts, ev, chunkSeq: chunk.seq });
    }
  }

  // ES2019+ garantisi: Array#sort stabildir → eşit `at` değerlerinde ekleme sırası kalır.
  events.sort((a, b) => a.at - b.at);

  return {
    events,
    startAt: events.length === 0 ? 0 : events[0].at,
    endAt: events.length === 0 ? 0 : events[events.length - 1].at,
  };
}

/* -------------------------------------------------------------------------- */
/* İnaktivite sıkıştırma (§22.7 "inaktivite atla")                              */
/* -------------------------------------------------------------------------- */

/** §22.7 — "ardışık olaylar arası > 5000 ms boşluk". */
export const DEFAULT_GAP_MS = 5000;
/** §22.7 — "→ 1000 ms olarak oynatılır". */
export const DEFAULT_COLLAPSED_MS = 1000;

export interface PlaybackEvent extends TimelineEvent {
  /** sıkıştırılmış (oynatma) zaman ekseni — ilk olay her zaman 0'dadır */
  playAt: number;
}

export interface CompressedTimeline {
  events: PlaybackEvent[];
  /** oynatmanın toplam süresi (ms, sıkıştırılmış eksende) */
  duration: number;
  /** kullanıcıya "N sn atlandı" demek için: kırpılan toplam boşluk (ms) */
  skippedMs: number;
}

/**
 * §22.7 — inaktivite atla. Boşluk `gapMs`i AŞIYORSA (kesin büyükse) `collapsedMs`e
 * indirilir; eşit ya da küçük boşluklar OLDUĞU GİBİ korunur — sıkıştırma, izlenen
 * etkileşimin ritmini bozmamalı, yalnız ölü zamanı kısaltmalıdır.
 */
export function compressGaps(
  events: readonly TimelineEvent[],
  gapMs: number = DEFAULT_GAP_MS,
  collapsedMs: number = DEFAULT_COLLAPSED_MS,
): CompressedTimeline {
  const out: PlaybackEvent[] = [];
  let play = 0;
  let skippedMs = 0;
  let prevAt = events.length === 0 ? 0 : events[0].at;

  for (const item of events) {
    // Negatif fark (saat kayması / bozuk kayıt) 0'a kırpılır: eksen geri akamaz.
    const gap = Math.max(0, item.at - prevAt);
    // collapsedMs > gap olan uçuk yapılandırmada boşluk UZAMASIN diye min alınır.
    const step = gap > gapMs ? Math.min(collapsedMs, gap) : gap;
    play += step;
    skippedMs += gap - step;
    out.push({ ...item, playAt: play });
    prevAt = item.at;
  }

  return { events: out, duration: play, skippedMs };
}

/**
 * Mutlak zamanı (epoch ms) sıkıştırılmış oynatma eksenine taşır.
 *
 * İşaretçiler §10 events ucundan gelir (kayıt akışından değil), yani MUTLAK eksendedir;
 * ilerleme çubuğu ise sıkıştırılmış eksendedir. Bu dönüşüm olmadan uzun bir inaktivite
 * sonrası gelen `js_error` işaretçisi çubukta yanlış yere düşerdi.
 */
export function mapToPlayTime(events: readonly PlaybackEvent[], at: number): number {
  if (events.length === 0) return 0;
  const first = events[0];
  const last = events[events.length - 1];
  if (at <= first.at) return first.playAt;
  if (at >= last.at) return last.playAt;

  // İkili arama: `lo` her zaman at'ten küçük/eşit son olay.
  let lo = 0;
  let hi = events.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (events[mid].at <= at) lo = mid;
    else hi = mid;
  }

  const a = events[lo];
  const b = events[hi];
  const realGap = b.at - a.at;
  if (realGap <= 0) return a.playAt;
  // Sıkıştırılmış adımın içinde oransal ilerle — eksen monoton kalır.
  return a.playAt + ((at - a.at) / realGap) * (b.playAt - a.playAt);
}

/* -------------------------------------------------------------------------- */
/* Timeline işaretçileri (§22.7)                                                */
/* -------------------------------------------------------------------------- */

export type MarkerKind = 'js_error' | 'rage' | 'page';

export interface TimelineMarker {
  /** olayın mutlak zamanı (epoch ms) */
  at: number;
  kind: MarkerKind;
  /** startAt..endAt aralığındaki konumu — yüzde (0..100) */
  pct: number;
}

/**
 * §22.7 — "js_error kırmızı, rage_click/dead_click turuncu, sayfa değişimi dikey çizgi".
 * Kaynak KAYIT AKIŞI DEĞİL, §10 events ucudur: kayıt yalnız DOM'u taşır; hangi tıkın
 * rage sayıldığı, hangi hatanın js_error olduğu sunucudaki türetme kurallarının işidir.
 */
const MARKER_BY_TYPE: Readonly<Record<string, MarkerKind>> = {
  js_error: 'js_error',
  rage_click: 'rage',
  dead_click: 'rage',
  page_view: 'page',
};

export interface SessionEventLike {
  /** olayın mutlak zamanı — epoch ms (§10 events ucu) */
  ts: number;
  type: string;
}

/**
 * Oturum olaylarını timeline işaretçilerine çevirir.
 *
 * Aralık dışına düşen olaylar ATILMAZ, uçlara kırpılır: kayıt oturumun tamamını
 * kapsamayabilir (buffer modunda yalnız son 60 sn yüklenir, §22.3) ve teşhis için
 * "hata kaydın sonunda/başında" bilgisi kaybolmamalıdır.
 */
export function markers(
  sessionEvents: readonly SessionEventLike[],
  startAt: number,
  endAt: number,
): TimelineMarker[] {
  const span = endAt - startAt;
  const out: TimelineMarker[] = [];

  for (const event of sessionEvents) {
    if (event === null || typeof event !== 'object') continue;
    if (typeof event.ts !== 'number' || !Number.isFinite(event.ts)) continue;
    /*
     * `hasOwn` ŞART: düz nesne aramasında `type: 'constructor'` (veya 'toString',
     * 'hasOwnProperty') prototipten bir FONKSİYON döndürür, `undefined` değil —
     * o zaman geçersiz bir `kind` işaretçi listesine sızar ve oynatıcıdaki
     * `MARKER_STYLE[kind]` araması undefined'a düşüp bileşeni çökertirdi.
     */
    const kind = Object.hasOwn(MARKER_BY_TYPE, event.type)
      ? MARKER_BY_TYPE[event.type]
      : undefined;
    if (kind === undefined) continue;
    // Tek anlık kayıt (span=0) yüzde üretemez; 0 uydurmak, NaN/∞ basmaktan güvenli.
    const pct = span > 0 ? clamp(((event.ts - startAt) / span) * 100, 0, 100) : 0;
    out.push({ at: event.ts, kind, pct });
  }

  return out.sort((a, b) => a.at - b.at);
}

/* -------------------------------------------------------------------------- */
/* Küçük yardımcılar                                                            */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* §35.5 — ağ satırları                                                        */
/* -------------------------------------------------------------------------- */

/** §35.5 satır rengi: 2xx nötr, 4xx turuncu, 5xx (ve yanıtsız) kırmızı. */
export type NetTone = 'ok' | 'warn' | 'error';

/** §35.5 — "süre > 2000 ms kalın". */
export const NET_SLOW_MS = 2000;

export interface NetRow {
  /** oynatma eksenindeki zaman (ms) — satıra tıklanınca buraya sarılır */
  playAt: number;
  method: string;
  path: string;
  status: number;
  ms: number;
  /** yanıt bayt sayısı — `content-length` yoksa alan YOKTUR (§35.1) */
  res?: number;
  tone: NetTone;
  slow: boolean;
}

/**
 * §35.5 — `status` → satır tonu.
 *
 * `status === 0` KIRMIZIDIR: SDK ağ hatasında (DNS, CORS, iptal) sıfır yazar ve §35.1
 * "hangisi hiç dönmedi" sorusunu açıkça kapsama alır. Nötr göstermek, teşhisteki en
 * ağır vakayı en sönük satır yapardı.
 */
export function netTone(status: number): NetTone {
  if (status === 0 || status >= 500) return 'error';
  if (status >= 400) return 'warn';
  return 'ok';
}

/**
 * §35.5 — oynatma eksenine oturtulmuş ağ olayları.
 *
 * Girdi `compressGaps` ÇIKTISIDIR (PlaybackEvent), ham timeline değil: panelde
 * gösterilen zaman ile tıklanınca gidilen zaman AYNI eksende olmalıdır, yoksa
 * inaktivite sıkıştırması olan bir kayıtta satıra tıklamak yanlış ana atlar.
 *
 * Bozuk/eksik alanlı olay ATLANIR — replay kayıp-toleranslıdır (§22.5) ve `path`siz
 * bir satır panelde hiçbir şey anlatmaz.
 */
export function netRows(events: readonly PlaybackEvent[]): NetRow[] {
  const rows: NetRow[] = [];
  for (const item of events) {
    const ev = item.ev;
    if (ev.t !== 'net') continue;
    const path = typeof ev.path === 'string' ? ev.path : '';
    if (path === '') continue;
    const status = typeof ev.status === 'number' && Number.isFinite(ev.status) ? ev.status : 0;
    const ms = typeof ev.ms === 'number' && Number.isFinite(ev.ms) && ev.ms >= 0 ? ev.ms : 0;
    const row: NetRow = {
      playAt: item.playAt,
      method: typeof ev.method === 'string' && ev.method !== '' ? ev.method : 'GET',
      path,
      status,
      ms,
      tone: netTone(status),
      slow: ms > NET_SLOW_MS,
    };
    if (typeof ev.res === 'number' && Number.isFinite(ev.res) && ev.res >= 0) row.res = ev.res;
    rows.push(row);
  }
  return rows;
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}

/**
 * §22.5 — chunk gövdesi ayrıştırılamamış/eksik olabilir (sunucu bozuk gövdede boş
 * dizi döner). Oynatıcı hiçbir koşulda çökmemeli: olay biçimine uymayan girdiler
 * sessizce elenir.
 */
export function normalizeChunkBody(raw: unknown): ReplayEventLike[] {
  if (!Array.isArray(raw)) return [];
  const out: ReplayEventLike[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue;
    const candidate = item as Partial<ReplayEventLike>;
    if (typeof candidate.t !== 'string') continue;
    if (typeof candidate.ts !== 'number' || !Number.isFinite(candidate.ts)) continue;
    out.push(candidate as ReplayEventLike);
  }
  return out;
}

/** Oynatıcı saati — `m:ss` (bir saati aşan kayıtlarda `h:mm:ss`). */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round((Number.isFinite(ms) ? ms : 0) / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
